import { useState, useEffect } from "react";
import styled from "styled-components";
import { SpaceProps } from "styled-system";

import Box from "../Box/Box";
import Typography from "../Typography/Typography";
import useThemeColor from "../useThemeColor.hook";
import Icon from "../Icon/Icon";
import ResponsiveLayout from "../ResponsiveLayout/ResponsiveLayout";
import Divider from "../Divider/Divider";

export interface FAQAccordionItem {
  id: string;
  analyticsLabel: string;
  headline: string;
  description: string;
}

export interface FAQAccordionProps extends SpaceProps {
  /**    - Represents the content of the accordion component. Accepts an array of AccordionItem. Each item in the array representing a question. AccordionItem has the following props:
  - id : string; // unique identifier of the item
  - headline: string; // the question (header of the item)
  - description: string; // a string representing an escaped HTML used to render the content of the accordion item (displayed when the item is expanded)
 */
  items: FAQAccordionItem[];
  /** Represents the color of the header title
   */
  headerColor?: string;
  /** Represents the color of the body (content)
   */
  bodyColor?: string;
  /** Represents the color of the expand/collapse button
   */
  iconColor?: string;
  /** Represents the color of the body links
   */
  linksColor?: string;
  /**
  Allows the user to specify the max width of the Accordion. Accepts numbers that represent the number of pixels, or strings for percentages, calc() functions, common css keywords (auto, fit-content, min-content, max-content) or arrays(string[] | number[]) for responsive values.
  (eg. maxWidth: 100 -> 100px; maxWidth: "100%" -> 100%;)
  */
}

interface AccordionHeaderProps {
  headerColor: string;
  iconColor: string;
  headline: string;
  index: number;
  expandedIndexes: number[];
  onButtonClick: (index: number) => void;
}

const AccordionContent = styled(Box)`
  transition: max-height 0.3s ease-in;
`;

const AnimatedIconContainer = styled(Box)`
  cursor: pointer;
  .collapse {
    animation: collapse 0.3s;
  }
  .expanded {
    animation: expand 0.3s;
    transform: rotate(-180deg);
  }

  @keyframes expand {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(-180deg);
    }
  }

  @keyframes collapse {
    from {
      transform: rotate(-180deg);
    }

    to {
      transform: rotate(0deg);
    }
  }
`;

const AccordionHeaderContainer = styled(Box)`
  cursor: pointer;
`;

const AccordionHeader = ({
  headerColor,
  iconColor,
  headline,
  index,
  expandedIndexes,
  onButtonClick,
}: AccordionHeaderProps) => {
  return (
    <AccordionHeaderContainer
      display="flex"
      justifyContent="space-between"
      width="100%"
      role="group"
      onClick={() => onButtonClick(index)}
      py={6}
    >
      <Typography variant="h6" textAlign="left" color={headerColor} m={0}>
        {headline}
      </Typography>
      <AnimatedIconContainer
        paddingX={0}
        mr={{ _: 0, desktop: 6 }}
        ml={6}
        height="auto"
        aria-controls={`section${index}`}
        aria-expanded={expandedIndexes.includes(index)}
        aria-label="toggle-button"
        role="button"
      >
        <Icon
          width="25px"
          height="25px"
          name="angle-down"
          weight="regular"
          color={iconColor}
          className={expandedIndexes.includes(index) ? "expanded" : "collapse"}
        />
      </AnimatedIconContainer>
    </AccordionHeaderContainer>
  );
};

export default function FAQAccordion({
  items,
  headerColor = "accordion-default-title-color",
  bodyColor = "informative-text-color",
  iconColor = "cta-primary-background-color-default",
  linksColor = "cta-button-text-color-hover",
  ...props
}: FAQAccordionProps) {
  const [expandedIndexes, setExpandedIndexes] = useState<number[]>(
    [] as number[]
  );
  const [overflowY, setOverflowY] = useState<"hidden" | "auto">("hidden");
  const linksHexColor = useThemeColor(linksColor);

  const bodyCSS = `
  line-height: 18.8px;
  transition: opacity 0.3s ease-in;
  a {
    text-decoration: none;
    color: ${linksHexColor};
  }
  * {
    margin-top: 10px;
    margin-bottom: 10px;
  }
  *:first-child {
    margin-top: 0px;
  }
  *:last-child {
    margin-bottom: 24px;
  }
`;

  useEffect(() => {
    setOverflowY("hidden");
  }, [expandedIndexes]);

  const parsedItems = items.map((item): FAQAccordionItem => {
    const parser = new DOMParser();
    const elements = parser.parseFromString(item.description, "text/html").body
      .childNodes;

    return {
      ...item,
      description: elements[0].nodeValue || item.description,
    };
  });

  const onButtonClick = (index: number) =>
    expandedIndexes.includes(index) ? collapseItem(index) : expandItem(index);

  const expandItem = (index: number) => {
    setExpandedIndexes([...expandedIndexes, index]);
    updateVerticalOverflow();
  };

  const collapseItem = (index: number) => {
    setOverflowY("hidden");
    const newExpandedIndexes = expandedIndexes.filter((item) => item !== index);
    setExpandedIndexes(newExpandedIndexes);
  };

  const updateVerticalOverflow = () => {
    setTimeout(() => setOverflowY("auto"), 300);
  };

  return (
    <ResponsiveLayout>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        role="main"
        {...props}
      >
        {parsedItems.map((item, index) => {
          const isExpanded = expandedIndexes.includes(index);
          const eventAction = isExpanded ? "collapse" : "expand";

          return (
            <Box
              aria-label="item-container"
              key={item.id}
              data-en="click"
              data-ec="accordion"
              data-ea={eventAction}
              data-el={item.analyticsLabel}
            >
              <AccordionHeader
                headerColor={headerColor}
                iconColor={iconColor}
                headline={item.headline}
                index={index}
                expandedIndexes={expandedIndexes}
                onButtonClick={onButtonClick}
              />
              <AccordionContent
                id={`section${index}`}
                className={isExpanded ? "expanded" : ""}
                role="region"
                aria-labelledby={`section${index}`}
                aria-label={`content${index}`}
                maxHeight={isExpanded ? "100vh" : 0}
                width={"100%"}
                pr={{ _: 36, tablet: 76, desktop: 132 }}
                overflowY={overflowY}
              >
                <Typography
                  variant="body"
                  color={bodyColor}
                  css={isExpanded ? bodyCSS : bodyCSS + `opacity: 0`}
                  htmlTag="div"
                >
                  <Box dangerouslySetInnerHTML={{ __html: item.description }} />
                </Typography>
              </AccordionContent>
              <Divider marginTop={0} marginBottom={0} />
            </Box>
          );
        })}
      </Box>
    </ResponsiveLayout>
  );
}
